import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';


const SalesFunnelVisualization = () => {
  const [funnelData, setFunnelData] = useState([]);
  const [selectedStage, setSelectedStage] = useState(null);
  const [timeRange, setTimeRange] = useState('today');

  useEffect(() => {
    // Mock funnel data
    const mockData = [
      {
        id: 'entry',
        name: 'Data Entry',
        value: 156,
        percentage: 100,
        color: 'bg-primary',
        textColor: 'text-primary',
        icon: 'FileText',
        description: 'Sales data submitted by field teams',
        bottleneck: false,
        conversionRate: 100,
        avgTime: '2.5 min'
      },
      {
        id: 'validation',
        name: 'Validation',
        value: 142,
        percentage: 91.0,
        color: 'bg-secondary',
        textColor: 'text-secondary',
        icon: 'CheckCircle',
        description: 'Data validated and verified for accuracy',
        bottleneck: true,
        conversionRate: 91.0,
        avgTime: '8.2 min'
      },
      {
        id: 'processing',
        name: 'Processing',
        value: 138,
        percentage: 88.5,
        color: 'bg-warning',
        textColor: 'text-warning',
        icon: 'Zap',
        description: 'Sales records processed and integrated',
        bottleneck: false,
        conversionRate: 97.2,
        avgTime: '3.1 min'
      },
      {
        id: 'collection',
        name: 'Collection',
        value: 124,
        percentage: 79.5,
        color: 'bg-success',
        textColor: 'text-success',
        icon: 'CreditCard',
        description: 'Payment collection completed',
        bottleneck: true,
        conversionRate: 89.9,
        avgTime: '45.3 min'
      },
      {
        id: 'completion',
        name: 'Completion',
        value: 118,
        percentage: 75.6,
        color: 'bg-accent',
        textColor: 'text-accent-foreground',
        icon: 'Target',
        description: 'Full sales cycle completed successfully',
        bottleneck: false,
        conversionRate: 95.2,
        avgTime: '1.2 min'
      }
    ];

    setFunnelData(mockData);
  }, [timeRange]);

  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setFunnelData(prev => prev.map(stage => ({
        ...stage,
        value: Math.max(0, stage.value + Math.floor((Math.random() - 0.5) * 6)),
        avgTime: stage.id === 'collection' 
          ? `${(45.3 + (Math.random() - 0.5) * 10).toFixed(1)} min`
          : stage.avgTime
      })));
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  const handleStageClick = (stage) => {
    setSelectedStage(selectedStage?.id === stage.id ? null : stage);
  };

  const getBottleneckIndicator = (stage) => {
    if (stage.bottleneck) {
      return (
        <div className="flex items-center space-x-1 text-error">
          <Icon name="AlertTriangle" size={12} />
          <span className="text-xs font-medium">Bottleneck</span>
        </div>
      );
    }
    return null;
  };

  const formatPercentage = (value) => `${value.toFixed(1)}%`;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Icon name="GitBranch" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Sales Funnel</h3>
        </div>

        <div className="flex items-center space-x-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="text-sm border border-border rounded-md px-3 py-1 bg-background text-foreground"
          >
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
        </div>
      </div>

      {/* Funnel Visualization */}
      <div className="space-y-4 mb-6">
        {funnelData.map((stage, index) => {
          const isSelected = selectedStage?.id === stage.id;
          const maxWidth = 100;
          const stageWidth = (stage.percentage / 100) * maxWidth;
          
          return (
            <div key={stage.id} className="relative">
              {/* Stage Bar */}
              <div
                className={`relative cursor-pointer transition-all duration-300 ${
                  isSelected ? 'transform scale-105' : 'hover:transform hover:scale-102'
                }`}
                onClick={() => handleStageClick(stage)}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${stage.color}/10`}>
                      <Icon name={stage.icon} size={16} className={stage.textColor} />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground">
                        {stage.name}
                      </h4>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-muted-foreground">
                          {stage.value} records
                        </span>
                        {getBottleneckIndicator(stage)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-sm font-semibold text-foreground">
                      {formatPercentage(stage.percentage)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {index > 0 && `${formatPercentage(stage.conversionRate)} conversion`}
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="relative h-8 bg-muted rounded-lg overflow-hidden">
                  <div
                    className={`h-full ${stage.color} transition-all duration-500 ease-out flex items-center justify-center`}
                    style={{ width: `${stageWidth}%` }}
                  >
                    <span className="text-xs font-medium text-white">
                      {stage.value}
                    </span>
                  </div>
                  
                  {/* Conversion Arrow */}
                  {index < funnelData.length - 1 && (
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                      <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                    </div>
                  )}
                </div>

                {/* Expanded Details */}
                {isSelected && (
                  <div className="mt-3 p-4 bg-muted/50 rounded-lg border border-border">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                          Description
                        </h5>
                        <p className="text-sm text-foreground">
                          {stage.description}
                        </p>
                      </div>
                      
                      <div>
                        <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                          Average Time
                        </h5>
                        <p className="text-sm font-semibold text-foreground">
                          {stage.avgTime}
                        </p>
                      </div>
                      
                      <div>
                        <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                          Status
                        </h5>
                        <div className="flex items-center space-x-2">
                          {stage.bottleneck ? (
                            <>
                              <Icon name="AlertTriangle" size={14} className="text-error" />
                              <span className="text-sm text-error font-medium">
                                Needs Attention
                              </span>
                            </>
                          ) : (
                            <>
                              <Icon name="CheckCircle" size={14} className="text-success" />
                              <span className="text-sm text-success font-medium">
                                Operating Normally
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-border">
        <div className="text-center">
          <div className="text-2xl font-bold text-foreground">
            {formatPercentage(funnelData[funnelData.length - 1]?.percentage || 0)}
          </div>
          <div className="text-sm text-muted-foreground">
            Overall Conversion
          </div>
        </div>
        
        <div className="text-center">
          <div className="text-2xl font-bold text-warning">
            {funnelData.filter(stage => stage.bottleneck).length}
          </div>
          <div className="text-sm text-muted-foreground">
            Active Bottlenecks
          </div>
        </div>
        
        <div className="text-center">
          <div className="text-2xl font-bold text-primary">
            {funnelData[0]?.value || 0}
          </div>
          <div className="text-sm text-muted-foreground">
            Total Entries Today
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesFunnelVisualization;